import React from "react";
import HabitCard from "./HabitCard.jsx";
import HabitComposer from "./HabitComposer.jsx";
import SportCard from "./SportCard.jsx";
import { DROP_TARGET_ATTR } from "../hooks/useDragDrop.js";
import { WEEKDAY_LABELS, weekdayIndex } from "../utils/date.js";
import { completionKey, isCompleted, isDayComplete } from "../utils/habits.js";
import { isSportHabit } from "../utils/sport.js";

/**
 * The written invitation to add a habit — the day's main add control.
 *
 * On an empty day it stands in for the old "nothing yet…" line, so the blank
 * space itself is the thing you tap; once the day has entries it moves to the
 * bottom and reads "add more…".
 */
function AddHabitLine({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="py-1.5 text-left font-hand text-base leading-tight text-ink-soft
                 transition-colors hover:text-accent"
    >
      {label}
    </button>
  );
}

/**
 * One day: a torn sheet holding that day's habits.
 * Also a drop target — the `data-daykey` attribute is what the drag hook
 * hit-tests against, and the `id` is the scroll anchor used on mobile.
 *
 * `placing` turns the whole sheet into one big button: a sport has been
 * designed in the header composer and is waiting to be dropped on a day.
 */
export default function DayColumn({
  date,
  dateKey,
  className = "",
  occurrences,
  completions,
  sportLogs,
  editMode,
  today,
  past,
  muted,
  isOver,
  dragSourceId,
  composing,
  placing,
  placingLabel = "",
  onToggle,
  onDelete,
  onAdd,
  onOpenSport,
  onCreate,
  onCloseComposer,
  onDragStart,
  onPickDay,
}) {
  const weekday = WEEKDAY_LABELS[weekdayIndex(date)];
  // A day with habits, all of them struck through, gets a quiet green tint.
  const cleared = isDayComplete(occurrences, completions);

  // Exactly one border colour wins, in this order of importance:
  // the active drop target, then today (which keeps its orange even once the
  // day is cleared), then the green of a finished day.
  const borderClass = isOver
    ? "border-accent"
    : today
      ? "border-accent"
      : cleared
        ? "border-done/50"
        : "";

  // While a sport is looking for a home the sheet itself is the control, so
  // the props that make it one are gathered here rather than inlined below.
  // Native <button> is not an option — a day sheet contains its own buttons.
  const pickProps = placing
    ? {
        role: "button",
        tabIndex: 0,
        "aria-label": placingLabel
          ? `Place ${placingLabel}`
          : `Place on ${dateKey}`,
        onClick: () => onPickDay(dateKey),
        onKeyDown: (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onPickDay(dateKey);
          }
        },
      }
    : {};

  // Sports share the habit list but always sink to the bottom of it, where
  // their dashed outline marks them out as a different kind of entry.
  const habitOccurrences = occurrences.filter(
    (occ) => !isSportHabit(occ.habit),
  );
  const sportOccurrences = occurrences.filter((occ) => isSportHabit(occ.habit));

  return (
    <section
      id={`day-${dateKey}`}
      {...{ [DROP_TARGET_ATTR]: dateKey }}
      {...pickProps}
      // Tailwind resolves competing utilities by CSS source order, not by the
      // order they appear here — so the border colour is picked once, up
      // front, rather than layered and hoped for.
      className={`sheet torn flex flex-col gap-1.5 p-3 transition-all duration-150 ${className} ${borderClass} ${
        past ? "bg-paper-deep/60" : ""
      } ${muted ? "border-dashed bg-paper-deep/40 shadow-none" : ""} ${
        cleared ? "bg-done-soft/60" : ""
      } ${today || isOver ? "shadow-paper-lift" : ""} ${
        isOver ? "scale-[1.01] bg-accent-soft/40" : ""
      } ${
        placing
          ? "cursor-pointer ring-1 ring-accent/40 hover:scale-[1.01] hover:bg-accent-soft/50 hover:shadow-paper-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          : ""
      }`}
    >
      <div
        className={`flex items-baseline justify-between gap-2 border-b border-dashed pb-1.5 ${
          cleared ? "border-done/40" : "border-line"
        }`}
      >
        <div className="flex items-baseline gap-2">
          <span
            className={`font-display text-sm uppercase tracking-tight ${today ? "text-accent" : ""}`}
          >
            {weekday}
          </span>
          <span className="font-hand text-xl leading-none text-ink-soft">
            {date.getDate()}
          </span>
        </div>
        {/* Both can show at once: today that has also been cleared still says
            so, it just keeps its orange marker as well. */}
        <span className="flex shrink-0 items-baseline gap-1.5">
          {cleared && (
            <span className="font-hand text-base text-done">done</span>
          )}
          {today && (
            <span className="font-hand text-base text-accent">today</span>
          )}
        </span>
      </div>

      {/* While picking a day, nothing inside the sheet may steal the tap —
          the sheet as a whole is the target. */}
      <div
        className={`flex flex-col gap-0.5 ${placing ? "pointer-events-none" : ""}`}
      >
        {habitOccurrences.map((occ) => (
          <HabitCard
            key={`${occ.habit.id}|${occ.baseDate}`}
            occurrence={occ}
            done={isCompleted(completions, occ.habit.id, occ.baseDate)}
            editMode={editMode}
            isDragSource={dragSourceId === `${occ.habit.id}|${occ.baseDate}`}
            onToggle={() => onToggle(occ.habit.id, occ.baseDate)}
            onDelete={(scope) => onDelete(occ.habit, occ.baseDate, scope)}
            onDragStart={(event) => onDragStart(event, occ)}
          />
        ))}

        {composing ? (
          <HabitComposer
            onCreate={(name, repeat) => onCreate(name, repeat, dateKey)}
            onClose={onCloseComposer}
          />
        ) : // On an empty day the add line is up in the list instead, where
        // "nothing yet…" used to sit — no need to offer it twice.
        occurrences.length > 0 ? (
          <AddHabitLine label="add more…" onClick={() => onAdd(dateKey)} />
        ) : (
          <AddHabitLine label="add habit…" onClick={() => onAdd(dateKey)} />
        )}

        {sportOccurrences.length !== 0 && !composing && (
          <div className="w-full border-b border-dashed border-line"></div>
        )}

        {/* Same list, but sports always come last and carry a dashed outline */}
        {sportOccurrences.map((occ) => (
          <SportCard
            key={`${occ.habit.id}|${occ.baseDate}`}
            occurrence={occ}
            values={sportLogs[completionKey(occ.habit.id, occ.baseDate)] || {}}
            done={isCompleted(completions, occ.habit.id, occ.baseDate)}
            editMode={editMode}
            isDragSource={dragSourceId === `${occ.habit.id}|${occ.baseDate}`}
            onOpen={() => onOpenSport(occ)}
            onDelete={(scope) => onDelete(occ.habit, occ.baseDate, scope)}
            onDragStart={(event) => onDragStart(event, occ)}
          />
        ))}
      </div>

      {/* Confirms what this particular tap will do, in the day's own corner */}
      {placing && (
        <p className="pointer-events-none pt-1 text-right font-hand text-base leading-tight text-accent">
          place here
        </p>
      )}
    </section>
  );
}
